import React from 'react';
import withStyles from 'isomorphic-style-loader/withStyles';
import _ from 'lodash';
import fruitData from './fruitData';
import s from './fruits.css';

const { fruits, nodes, rootNode } = fruitData;

const innerXDim = 680;
const padding = 10;
const axisHeight = 50;
const xDim = innerXDim + 2 * padding;
const nodeHeight = 32;
const nodeWidth = 100;
const nodeSpacing = 16;
const innerYDim = (nodeHeight + nodeSpacing) * fruits.length - nodeSpacing;
const yDim = innerYDim + padding + 2 * axisHeight;
const maxMya = 200;
const minMya = 0.0;
const axisMyas = [
  1,
  5,
  15,
  35,
  65,
  100,
  150,
  200,
];
const lineHeight = 16;

const scale = (rawX) => Math.sqrt(rawX);
const scaledMaxX = scale(maxMya);
const scaledMinX = scale(minMya);
const xRange = innerXDim - 1.5 * nodeWidth;

const xFromMya = (mya) => {
  return nodeWidth / 2 +
    xRange * (scaledMaxX - scale(mya)) / (scaledMaxX - scaledMinX);
};
const xFromNode = (node) => {
  const mya = (() => {
    if (node.mya) {
      return node.mya;
    } else if (node.myaLb) {
      return Math.sqrt(node.myaLb * node.myaUb);
    }
    return minMya;
  })();
  const myaX = xFromMya(mya);
  if (node.isFruit) {
    return myaX + nodeWidth / 2;
  }
  return myaX;
};

fruits.forEach((fruit, i) => {
  fruit.yMin = i * (nodeHeight + nodeSpacing);
});
const tryFillY = (node) => {
  if (node.yMin === undefined) {
    tryFillY(node.left);
    tryFillY(node.right);
    node.yMin = (node.left.yMin + node.right.yMin) / 2;
  }
};
tryFillY(rootNode);

nodes.forEach((node) => {
  node.xCenter = xFromNode(node);
  node.width = node.width || nodeWidth;
  node.xMin = node.xCenter - node.width / 2;
  node.xMax = node.xCenter + node.width / 2;
});

const getOpacity = (bool) => {
  return bool ? 1 : 0.4;
};

const getEcaNames = (node) => {
  if (node.bioNames) {
    return node.bioNames;
  } else if (Array.isArray(node.name)) {
    return [node.name.join(' ')];
  } else if (node.name) {
    return [node.name];
  }
};

const formatMya = (mya) => {
  if (mya === undefined) {
    return '0';
  } else if (mya >= 1) {
    return `${mya} million`;
  }
  return `${mya * 1000} thousand`;
};

const renderLines = (node, i) => {
  if (!node.left) {
    return [];
  }

  const baseKey = `line-${i}`;
  const x = node.xCenter;
  const y = node.yMin + nodeHeight / 2;
  const leftY = node.left.yMin + nodeHeight / 2;
  const rightY = node.right.yMin + nodeHeight / 2;
  const leftX = node.left.xCenter;
  const rightX = node.right.xCenter;
  const xp = x;
  const shadeLeft = node.left.selected && node.selected;
  const shadeRight = node.right.selected && node.selected;
  const leftOpacity = getOpacity(shadeLeft);
  const rightOpacity = getOpacity(shadeRight);
  return [
    // (<line x1={x} x2={xp} y1={y} y2={y} key={`${baseKey}-a`} className={s.line}/>),
    (<line x1={xp} x2={xp} y1={leftY} y2={y} key={`${baseKey}-a`} className={s.line} opacity={leftOpacity} />),
    (<line x1={xp} x2={xp} y1={y} y2={rightY} key={`${baseKey}-b`} className={s.line} opacity={rightOpacity} />),
    (<line x1={xp} x2={leftX} y1={leftY} y2={leftY} key={`${baseKey}-c`} className={s.line} opacity={leftOpacity} />),
    (<line x1={xp} x2={rightX} y1={rightY} y2={rightY} key={`${baseKey}-d`} className={s.line} opacity={rightOpacity} />),
  ];
};

const renderGuidelines = () => {
  return axisMyas.map((mya) => {
    const x = xFromMya(mya);
    return (
      <line x1={x} x2={x} y1="0" y2={innerYDim} key={`gl-${mya}`} className={s.guideline} />
    );
  });
};

const renderAxis = (yPos) => {
  const y = axisHeight / 2;
  const myaLabels = axisMyas.map((mya) => {
    const x = xFromMya(mya);
    const key = `axislabel-${mya}`;
    return (
      <text x={x} y={y} className={s.axislabel} key={key}>
        {mya}
      </text>
    );
  });
  return (
    <g width={innerXDim} height={axisHeight} transform={`translate(0,${yPos})`}>
      {myaLabels}
      <text x={innerXDim} y={y} className={s.axistitle}>
        million years ago
      </text>
    </g>
  );
};

class Fruits extends React.Component {
  // propTypes: {},
  constructor(props) {
    super(props);
    this.state = {
      selection: new Set([]),
      hovered: undefined,
    };
  }

  setSelection(node, on) {
    const { name } = node;
    const newSet = new Set([...this.state.selection]);
    if (on) {
      newSet.add(name);
    } else {
      newSet.delete(name);
    }
    this.setState({
      selection: newSet,
      hovered: undefined,
    });
  }

  setHovered(node, on) {
    const value = on ? node.name : undefined;
    this.setState({
      hovered: value,
    });
  }

  calculateEffectiveSelection() {
    const effectiveSelection = new Set([...this.state.selection]);
    const { hovered } = this.state;
    if (hovered) {
      if (effectiveSelection.has(hovered)) {
        effectiveSelection.delete(hovered);
      } else {
        effectiveSelection.add(hovered);
      }
    }
    this.effectiveSelection = effectiveSelection;
  }

  calculateEca(node) {
    const { effectiveSelection } = this;
    const numSelected = effectiveSelection.size || fruits.length;
    if (!node.left) {
      if (effectiveSelection.has(node.name) || effectiveSelection.size === 0) {
        node.selected = true;
        node.weight = 1;
        if (numSelected === 1) {
          this.eca = node;
        }
      } else {
        node.selected = false;
        node.weight = 0;
      }
      return;
    }

    this.calculateEca(node.left);
    this.calculateEca(node.right);
    if (node.left.weight === numSelected) {
      node.selected = false;
      node.weight = 0;
      return;
    }
    if (node.right.weight === numSelected) {
      node.selected = false;
      node.weight = 0;
      return;
    }

    node.selected = node.left.selected || node.right.selected;
    node.weight = node.left.weight + node.right.weight;
    if (node.weight === numSelected) {
      this.eca = node;
    }
  }

  renderNode(node, i) {
    if (!node.name || (!node.isFruit && !node.alwaysSelected && (this.eca !== node))) {
      return;
    }

    const transform = `translate(${node.xMin},${node.yMin})`;
    const key = `node-${i}`;
    const { selected } = node;
    const opacity = getOpacity(selected);

    let maybeClicker;
    const { width } = node;
    const nameLines = Array.isArray(node.name) ? node.name : [node.name];
    const height = nodeHeight + (nameLines.length - 1) * lineHeight;
    if (node.isFruit) {
      const reallySelected = this.state.selection.has(node.name);
      const onClick = () => {
        this.setSelection(node, !reallySelected);
      };
      const onMouseEnter = () => {
        this.setHovered(node, true);
      };
      const onMouseLeave = () => {
        this.setHovered(node, false);
      };
      maybeClicker = (
        <rect
          width={width}
          height={height}
          onClick={onClick}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
          className={s.clicker}
        />
      );
    }

    const strokeWidth = node.name === this.state.hovered ? 8 : 5;
    const textWeight = node === this.eca ? 1000 : 500;
    const nameTexts = nameLines.map((nameLine, j) => (
      <text
        x={width / 2}
        y={nodeHeight / 2 + j * lineHeight}
        className={s.nodetext}
        opacity={opacity}
        fontWeight={textWeight}
        key={`nameline-${i}-${j}`}
      >
        {nameLine}
      </text>
    ));
    return (
      <g transform={transform} key={key}>
        <rect
          width={width}
          height={height}
          className={s.noderect}
          stroke={node.color || 'black'}
          strokeWidth={strokeWidth}
          strokeOpacity={opacity}
        />
        {nameTexts}
        {maybeClicker}
      </g>
    );
  }

  renderDesc() {
    const selectedFruitSpans = fruits
      .filter((x) => x.selected)
      .map((fruit) => {
        return (
          <div
            style={{ color: fruit.color }}
            key={`fruittext-${fruit.name}`}
            className={s.fruitname}
          >
            &nbsp;
            {fruit.name}
          </div>
        );
      });

    let namedEca = this.eca;
    let names = getEcaNames(this.eca);
    while (!names) {
      namedEca = namedEca.parent;
      names = getEcaNames(namedEca);
    }
    let ecaText;
    const { mya, myaLb, myaUb } = this.eca;
    if (myaLb) {
      ecaText = `${formatMya(myaLb)} to ${formatMya(myaUb)}`;
    } else {
      ecaText = formatMya(mya);
    }

    return (
      <div>
        <span className={s.desckey}>
          Fruits selected:
        </span>
        {selectedFruitSpans}
        <br />
        <span className={s.desckey}>
          Most recent common ancestor around:
        </span>
        &nbsp;
        {ecaText} years ago
        <br />
        <span className={s.desckey}>
          A biologist might call all these fruits:
        </span>
        &nbsp;
        "{names.join('" or "')}"
      </div>
    );
  }

  render() {
    this.eca = undefined;
    this.calculateEffectiveSelection();
    this.calculateEca(rootNode);
    const innerTransform = `translate(${padding}, ${padding})`;
    const drawnGuidelines = renderGuidelines();
    const drawnLines = _.flatMap(nodes, renderLines);
    const drawnNodes = nodes.map((node, i) => this.renderNode(node, i));
    return (
      <div>
        <svg width={xDim} height={yDim}>
          <g width={innerXDim} height={innerYDim + 2 * axisHeight} transform={innerTransform}>
            {renderAxis(0)}
            <g width={innerXDim} height={innerYDim} transform={`translate(0,${axisHeight})`}>
              {drawnGuidelines}
              {drawnLines}
              {drawnNodes}
            </g>
            {renderAxis(innerYDim + axisHeight)}
          </g>
        </svg>
        {this.renderDesc()}
      </div>
    );
  }
}

export default withStyles(s)(Fruits);



// WEBPACK FOOTER //
// src/components/Fruits/Fruits.js